// frontend/app/components/CollectionProgressBar.js
import React, { useEffect, useRef, memo } from "react";
import { View, Text, StyleSheet, Animated } from "react-native";
import { useTranslation } from "../i18n/useTranslation";

/**
 * A refined progress bar component with color-coded states
 * - Red: < 33%
 * - Yellow: 33-66%
 * - Green: > 66%
 */
const CollectionProgressBar = memo(function CollectionProgressBar({ progress = null, label = null, isDarkMode = false, goalPercent = null, badge = null }) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t("Collection Progress");
  const animatedWidth = useRef(new Animated.Value(0)).current;
  const displayProgress = progress ?? 0;
  const hasGoal = typeof goalPercent === "number";
  const goalReached = hasGoal && displayProgress >= goalPercent;

  // Theme colors
  const themeColors = isDarkMode
    ? {
        container: "#0f172a",
        card: "#1e293b",
        cardBorder: "rgba(100, 116, 139, 0.2)",
        cardShadow: "#000",
        labelText: "#94a3b8",
        percentageText: "#f1f5f9",
      }
    : {
        container: "#f7f9fc",
        card: "#ffffff",
        cardBorder: "rgba(0, 119, 182, 0.08)",
        cardShadow: "#0077b6",
        labelText: "#64748b",
        percentageText: "#0f172a",
      };

  // Determine color based on progress – track uses a neutral tone that works in both modes
  const getProgressColor = (value) => {
    if (value < 33) return {
      primary: "#ef4444",
      secondary: "#fca5a5",
      track: "rgba(239, 68, 68, 0.15)"
    };
    if (value < 66) return {
      primary: "#f59e0b",
      secondary: "#fbbf24",
      track: "rgba(245, 158, 11, 0.15)"
    };
    return {
      primary: "#10b981",
      secondary: "#34d399",
      track: "rgba(16, 185, 129, 0.15)"
    };
  };

  const colors = getProgressColor(displayProgress);

  useEffect(() => {
    // Animate the progress bar on mount or update
    Animated.spring(animatedWidth, {
      toValue: displayProgress,
      useNativeDriver: false,
      tension: 40,
      friction: 8,
    }).start();
  }, [displayProgress]);

  const widthInterpolation = animatedWidth.interpolate({
    inputRange: [0, 100],
    outputRange: ["0%", "100%"],
    extrapolate: "clamp",
  });

  return (
    <View style={[styles.container, { backgroundColor: themeColors.container }]}>
      <View style={[
        styles.card,
        {
          backgroundColor: themeColors.card,
          borderColor: themeColors.cardBorder,
          shadowColor: themeColors.cardShadow,
        }
      ]}>
        <View style={styles.header}>
          <View style={styles.headerSide}>
            {badge ? (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{badge}</Text>
              </View>
            ) : null}
            <Text style={[styles.labelText, { color: themeColors.labelText }]}>{resolvedLabel}</Text>
          </View>
          <View style={styles.headerSide}>
            {hasGoal ? (
              <Text style={[styles.goalText, { color: themeColors.labelText }]}>
                {`${t("Goal")} ${goalPercent}%`}
              </Text>
            ) : null}
            <Text style={[styles.percentageText, { color: goalReached ? "#10b981" : themeColors.percentageText }]}>
              {goalReached ? "✓ " : ""}{displayProgress}%
            </Text>
          </View>
        </View>

        <View style={styles.progressWrapper}>
          {/* Background track */}
          <View style={[styles.track, { backgroundColor: colors.track }]} />

          {/* Animated fill */}
          <Animated.View
            style={[
              styles.fill,
              {
                width: widthInterpolation,
                backgroundColor: colors.primary,
              },
            ]}
          >
            {/* Gradient shimmer overlay */}
            <View style={[styles.shimmer, { backgroundColor: colors.secondary }]} />

            {/* Glossy highlight */}
            <View style={styles.highlight} />
          </Animated.View>

          {/* Goal marker: where the challenge threshold sits on the bar */}
          {hasGoal ? (
            <View
              pointerEvents="none"
              style={[
                styles.goalMarker,
                {
                  left: `${Math.min(100, Math.max(0, goalPercent))}%`,
                  backgroundColor: goalReached ? "#10b981" : "#0b2a63",
                },
              ]}
            />
          ) : null}
        </View>
      </View>
    </View>
  );
});

export default CollectionProgressBar;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 10,  // Reduced from 16
    paddingVertical: 8,     // Reduced from 14
  },
  card: {
    borderRadius: 10,       // Reduced from 12
    padding: 10,            // Reduced from 16
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,        // Reduced from 12
    elevation: 2,           // Reduced from 3
    borderWidth: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,        // Reduced from 12
  },
  labelText: {
    fontSize: 11,           // Reduced from 13
    fontWeight: "600",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  percentageText: {
    fontSize: 18,           // Reduced from 24
    fontWeight: "700",
    letterSpacing: -0.3,
  },
  headerSide: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  badge: {
    backgroundColor: "#e7c46a",
    borderRadius: 999,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  badgeText: {
    color: "#0b2a63",
    fontSize: 10.5,
    fontWeight: "800",
    letterSpacing: 0.3,
  },
  goalText: {
    fontSize: 11,
    fontWeight: "600",
  },
  goalMarker: {
    position: "absolute",
    width: 2,
    height: 14,
    top: -3,
    borderRadius: 1,
  },
  progressWrapper: {
    position: "relative",
    height: 8,              // Reduced from 12
    justifyContent: "center",
  },
  track: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 8,              // Reduced from 12
    borderRadius: 4,        // Reduced from 8
    overflow: "hidden",
  },
  fill: {
    position: "absolute",
    left: 0,
    height: 8,              // Reduced from 12
    borderRadius: 4,        // Reduced from 8
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 3,
    elevation: 1,
  },
  shimmer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.4,
  },
  highlight: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.35)",
    borderTopLeftRadius: 4,   // Reduced from 8
    borderTopRightRadius: 4,  // Reduced from 8
  },
});
